import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

// Long/short hues — teal de l'accent primaire pour les longs, rouge PnL pour
// les shorts : le meme rouge que tous les autres widgets (--edgely-pnl-negative,
// cf. StreakAnalysis, DrawdownCurve, Heatmap...). Choix demande cote produit :
// le couple teal/rouge se lit immediatement comme achat/vente, la ou le couple
// categoriel series[0]/series[1] passait par un bleu marine sans signification.
// Les tokens --chart-marker-* restent reserves aux marqueurs de trade sur les
// graphiques en chandeliers : ils visent l'ecart maximal avec les bougies
// vertes/rouges, pas l'harmonie avec l'accent de la marque.
function longShortColors() {
  const c = chartColors();
  return { long: c.series[0], short: c.negative };
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function fmtMoney(v: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)} $`;
}

/** Stacked Long|Short split bar — proportion based on trade count. */
function SplitBar({
  longPct,
  shortPct,
  colorLong,
  colorShort,
  thick,
}: {
  longPct: number;
  shortPct: number;
  colorLong: string;
  colorShort: string;
  thick?: boolean;
}) {
  return (
    <div className={`flex w-full overflow-hidden rounded-full bg-muted/30 ${thick ? "h-3" : "h-2"}`}>
      <div style={{ width: `${longPct}%`, backgroundColor: colorLong }} className="h-full transition-all" />
      <div style={{ width: `${shortPct}%`, backgroundColor: colorShort }} className="h-full transition-all" />
    </div>
  );
}

/** One detailed row: side dot + label, then P&L · win rate · trade count. */
function SideRow({
  color,
  label,
  pnl,
  winRate,
  count,
  winRateLabel,
}: {
  color: string;
  label: string;
  pnl: number;
  winRate: number;
  count: number;
  winRateLabel: string;
}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className="w-12 shrink-0 font-mono font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="flex-1 text-right font-mono font-bold" style={{ color: pnlColor(pnl) }}>{fmtMoney(pnl)}</span>
      <span className="w-14 text-right font-mono text-muted-foreground">{winRate.toFixed(0)}% {winRateLabel}</span>
      <span className="w-8 text-right font-mono text-muted-foreground">{count}</span>
    </div>
  );
}

export default function LongShortWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, t } = useDashboardData();
  const containerRef = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);
  // Re-lu a chaque changement de theme (les tokens CSS ont alors de nouvelles valeurs).
  const { long: colorLong, short: colorShort } = useMemo(() => longShortColors(), [isDark]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      // 1×1 (or very small): show the split bar only.
      setCompact(height < 150 || width < 220);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const header = (
    <CardSectionHeader
      label={t.dashboard.widgets.longShort}
      isDark={isDark}
      compact
      rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
    />
  );

  if (!analyticsData || (analyticsData.longShort.longCount === 0 && analyticsData.longShort.shortCount === 0)) {
    return (
      <div ref={containerRef} className="h-full">
        <Card className="h-full flex flex-col">
          {header}
          <CardContent className="flex-1 p-4">
            <EmptyState label={t.analytics.noDataAvailable} />
          </CardContent>
        </Card>
      </div>
    );
  }

  const ls = analyticsData.longShort;
  const totalCount = ls.longCount + ls.shortCount;
  const longPct = totalCount > 0 ? (ls.longCount / totalCount) * 100 : 50;
  const shortPct = 100 - longPct;
  const dominantIsLong = longPct >= shortPct;
  const dominantPct = Math.round(dominantIsLong ? longPct : shortPct);

  // ── Table view ───────────────────────────────────────────────────────────
  if (viewMode === "table") {
    const tableColumns = [
      { key: "side", label: t.analytics.sideLabel, align: "left" as const },
      { key: "count", label: t.analytics.trades, align: "right" as const },
      { key: "pnl", label: t.analytics.pnlLabel, align: "right" as const },
      { key: "winRate", label: t.analytics.winRateLabel, align: "right" as const },
    ];
    const rowFor = (label: string, pnl: number, count: number, winRate: number) => ({
      side: label,
      count,
      pnl: (
        <span className={pnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>{fmtMoney(pnl)}</span>
      ),
      winRate: `${winRate.toFixed(1)}%`,
    });
    const tableRows = [
      rowFor("Long", ls.longPnl, ls.longCount, ls.longWinRate),
      rowFor("Short", ls.shortPnl, ls.shortCount, ls.shortWinRate),
    ];
    return (
      <div ref={containerRef} className="h-full">
        <Card className="h-full flex flex-col">
          {header}
          <CardContent className="flex-1 min-h-0 px-4 pb-4 pt-2">
            <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.noDataAvailable} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Directional insight: which side performs better by total P&L.
  const insight =
    ls.longPnl > ls.shortPnl
      ? t.analytics.longPerforming
      : ls.shortPnl > ls.longPnl
      ? t.analytics.shortPerforming
      : t.analytics.equalPerformance;

  // ── Compact (1×1) — split bar + dominant share only ──────────────────────
  if (compact) {
    return (
      <div ref={containerRef} className="h-full">
        <Card className="h-full flex flex-col">
          {header}
          <CardContent className="flex-1 flex flex-col justify-center gap-2 px-4 pb-4 pt-2">
            <SplitBar longPct={longPct} shortPct={shortPct} colorLong={colorLong} colorShort={colorShort} thick />
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-lg font-bold" style={{ color: dominantIsLong ? colorLong : colorShort }}>
                {dominantPct}%
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-wide widget-label">
                {dominantIsLong ? "Long" : "Short"}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Normal — split bar + detailed rows + insight ─────────────────────────
  return (
    <div ref={containerRef} className="h-full">
      <Card className="h-full flex flex-col">
        {header}
        <CardContent className="flex-1 flex flex-col justify-center gap-3 px-4 pb-4 pt-2">
          <div className="flex flex-col gap-1.5">
            <SplitBar longPct={longPct} shortPct={shortPct} colorLong={colorLong} colorShort={colorShort} />
            <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
              <span>{Math.round(longPct)}% Long</span>
              <span>{Math.round(shortPct)}% Short</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <SideRow
              color={colorLong}
              label="Long"
              pnl={ls.longPnl}
              winRate={ls.longWinRate}
              count={ls.longCount}
              winRateLabel={t.analytics.winRate}
            />
            <SideRow
              color={colorShort}
              label="Short"
              pnl={ls.shortPnl}
              winRate={ls.shortWinRate}
              count={ls.shortCount}
              winRateLabel={t.analytics.winRate}
            />
          </div>
          <p className="text-center text-[11px] font-medium text-muted-foreground">{insight}</p>
        </CardContent>
      </Card>
    </div>
  );
}
